import { Controller, useFormContext, useWatch } from "react-hook-form";
import { View } from "react-native-ui-lib";
import {
  Button,
  Label,
  TextField,
  Text,
  Tooltip,
} from "@towersystems/roam-common-native";
import { ProductFormValues, FormInputProps } from "../../types";
import React from "react";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "../../../../../../utilities/i18n";

export type StockNumberFormInputProps = FormInputProps & {
  onGenerateBarcodePress?: () => Promise<string | undefined | void>;
  onStockNumberFormInputBlur?: () => void;
};

export const StockNumberFormInput = ({
  onGenerateBarcodePress,
  onStockNumberFormInputBlur,
}: StockNumberFormInputProps) => {
  const { t } = useTranslation("product");
  const { control, formState, setValue } = useFormContext<ProductFormValues>();
  const { errors } = formState;

  const globalDisable = useWatch({ control, name: "globalDisable" });

  const handleOnGenerateBarcodePress = React.useCallback(async () => {
    if (!onGenerateBarcodePress) {
      return;
    }

    const barcode = await onGenerateBarcodePress();
    if (barcode) {
      setValue("stockNumber", barcode, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [setValue, onGenerateBarcodePress]);

  const trailingMarkup = onGenerateBarcodePress ? (
    <Tooltip text="Generate Barcode">
      <View>
        <Button
          size="compact"
          onPress={handleOnGenerateBarcodePress}
          disabled={globalDisable}
        >
          <Text color="action">
            <Ionicons name="barcode" size={24} />
          </Text>
        </Button>
      </View>
    </Tooltip>
  ) : undefined;

  return (
    <View>
      <Label required label={t("form.barcode.label")}></Label>
      <Controller
        name="stockNumber"
        control={control}
        disabled={globalDisable}
        render={({
          field: { onChange, value, disabled },
          fieldState: { error },
        }) => (
          <TextField
            value={value ?? ""}
            onChangeText={onChange}
            onBlur={onStockNumberFormInputBlur}
            readonly={disabled}
            validationMessage={
              error
                ? error.message
                : errors.root?.stockNumber?.message ?? undefined
            }
            placeholder={t("form.barcode.placeholder")}
            trailingAccessory={trailingMarkup}
          />
        )}
      />
    </View>
  );
};
